<script>
import useVuelidate from '@vuelidate/core';

/* COMPOSANTS */
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import GaView from '@components/v2/layout/GaView';
import AkInputText from '@components/v2/input/AkInputText';
import AkCalendar from '@components/v2/input/AkCalendar';
import AkButtonValidation from '@components/v2/general/AkButtonValidation';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import messageMixin from '@mixins/messageMixin';
import loaderMixin from '@mixins/loaderMixin';

/* SERVICES */
import otaSettingsService from '../../services/otaSettingsService';
import AkLoader from '../../components/general/AkLoader.vue';
import reservationSiteConst from '../../mixins/const/reservationSiteConst';
import AkInputPercentage from '../../components/v2/input/AkInputPercentage.vue';
import AkYesNoButton from '../../components/v2/input/AkYesNoButton.vue';
import {helpers, required, requiredIf} from '@vuelidate/validators';
import StepInfo from '../onboarding/v3/step1/step_info/StepInfo.vue';
import AkDropdown from '../../components/v2/input/AkDropdown.vue';
import ruleCommissionConst from '../../mixins/const/ruleCommissionConst';
import AkInputNumber from '../../components/v2/input/AkInputNumber.vue';

export default {
  components: {
    AkInputNumber,
    AkDropdown,
    StepInfo,
    AkYesNoButton,
    AkInputPercentage,
    AkLoader,
    AkButtonValidation,
    AkInputText,
    AkCalendar,
    GaView,
    AkFormSubmitted,
  },
  mixins: [randomRef, utilsMixin, messageMixin, loaderMixin, reservationSiteConst, ruleCommissionConst],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'settings.ota_settings',
    };
  },
  data() {
    return {
      loading: true,
      submitted: false,
      settings: [],
    };
  },
  validations() {
    return {
      settings: {
        $each: helpers.forEach({
          commission: {
            required: requiredIf((_, item) => item.commission !== null),
          },
          rule: {
            required: requiredIf((_, item) => item.rule !== null),
          },
          tvaCommission: {
            required: requiredIf((_, item) => !!item?.tva && item.tvaCommission !== null),
          },
          minimumFacturation: {
            required: requiredIf((_, item) => !!item?.minimum && item.minimumFacturation !== null),
          },
          isGuestadomTdsCollector: {required},
        }),
      },
    };
  },
  mounted() {
    this.$refs.loader.load();
    otaSettingsService
      .settings()
      .then((data) => {
        this.settings = data;
      })
      .finally(() => {
        this.$refs.loader.hide();
      });
  },
  methods: {
    update() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.btnValid.init();
        return;
      }

      otaSettingsService
        .update(this.settings)
        .then((data) => {
          this.settings = data;
          this.addSuccess(this.$t('updated'));
        })
        .finally(() => {
          this.$refs.btnValid.init();
          this.submitted = false;
        });
    },
    organismesCollecteursTds(key) {
      return [
        {
          label: 'Guestadom',
          value: true,
        },
        {
          label: key,
          value: false,
        },
      ];
    },
  },
};
</script>

<template>
  <GaView details>
    <template #content>
      <AkLoader ref="loader" message="Récupération en cours">
        <template #content>
          <AkFormSubmitted
            :submitted="submitted"
            reference="form"
            @submit="update()"
            class="flex flex-col md:gap-4 gap-2">
            <div class="grid grid-cols-2 gap-4">
              <div v-for="(d, idx) of this.settings" class="bg-white rounded-xl p-4 gap-4 flex flex-col" :key="idx">
                <div class="flex flex-row gap-4 items-center">
                  <span class="logo-site" :class="iconReservationFromKey(d.key)" />
                  <span class="text-grey-900 text-md font-medium">{{ displayReservationSiteFromKey(d.key) }}</span>
                </div>
                <div class="flex flex-row items-center gap-4">
                  <AkInputPercentage
                    v-model="d.commission"
                    label="Commission"
                    class="w-max"
                    :validator="v$.settings.$each.$response.$data[idx].commission"
                    :submitted="submitted"
                    :disabled="d.commission === null"
                    :style="{cursor: d.commission === null ? 'not-allowed' : 'auto'}" />
                  <AkDropdown
                    v-model="d.rule"
                    label="Règle de calcul"
                    :options="ruleCommissionConst"
                    :submitted="this.submitted"
                    :validator="v$.settings.$each.$response.$data[idx].rule"
                    option-label="label"
                    option-value="value"
                    :disabled="d.rule === null"
                    :style="{cursor: d.rule === null ? 'not-allowed' : 'auto'}" />
                </div>
                <div class="flex flex-row items-center gap-4">
                  <AkYesNoButton
                    v-model="d.minimum"
                    label="Minimum"
                    class="w-max"
                    :disabled="d.minimum === null"
                    :style="{cursor: d.minimum === null ? 'not-allowed' : 'auto'}" />

                  <AkInputNumber
                    v-if="d.minimum"
                    v-model="d.minimumFacturation"
                    class="flex-1"
                    suffix="€"
                    :validator="v$.settings.$each.$response.$data[idx].minimumFacturation"
                    label="Minimum de commission"
                    :submitted="submitted"
                    :disabled="d.minimum === null"
                    :style="{cursor: d.minimum === null ? 'not-allowed' : 'auto'}" />
                </div>
                <div class="flex flex-row items-center gap-4">
                  <AkYesNoButton
                    v-model="d.tva"
                    label="TVA"
                    class="w-max"
                    :disabled="d.tva === null"
                    :style="{cursor: d.tva === null ? 'not-allowed' : 'auto'}" />

                  <AkInputPercentage
                    v-if="d.tva"
                    v-model="d.tvaCommission"
                    class="flex-1"
                    :validator="v$.settings.$each.$response.$data[idx].tvaCommission"
                    label="Pourcentage de TVA"
                    :submitted="submitted"
                    :disabled="d.tvaCommission === null"
                    :style="{cursor: d.tvaCommission === null ? 'not-allowed' : 'auto'}" />
                </div>
                <div class="flex flex-row items-center gap-4">
                  <AkDropdown
                    v-model="d.isGuestadomTdsCollector"
                    label="Organisme collecteur TDS"
                    :options="organismesCollecteursTds(d.key)"
                    :submitted="this.submitted"
                    :validator="v$.settings.$each.$response.$data[idx].isGuestadomTdsCollector"
                    option-label="label"
                    option-value="value" />
                </div>
              </div>
            </div>

            <div class="flex md:justify-end justify-center md:mt-4 mt-2">
              <AkButtonValidation ref="btnValid" :label="$t('update')" @action="this.update"></AkButtonValidation>
            </div>
          </AkFormSubmitted>
        </template>
      </AkLoader>
    </template>
  </GaView>
</template>
